import * as React from 'react'
// libs
import { useTheme } from '@mui/material'
import { isDesktop, isMobile } from 'react-device-detect'
// components
import { Option } from 'components/FormElements/components/index'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from 'components/FormElements/components/InputLabel'
import ReactSelect, { GroupBase, Props, StylesConfig } from 'react-select'
import CreatableSelect, { CreatableProps } from 'react-select/creatable'
// types
import { Theme } from '@mui/material'
import { CreatableSelectOptionClick } from 'components/FormElements/ReduxForm/ReduxCreatableSelect'
// views
import ErrorMessage, { ErrorMessageWrapper } from 'components/UI/ErrorMessage'
import { getDefaultSelectStyles, Icon, Indent, reactSelectTheme, SelectContainer, SelectWrapper } from './views'
// utils
import { getOptionsWithClearValue } from 'utils/form/select'

export interface IOption {
  value: string
  label: string

  [key: string]: any
}

export interface CustomSelectProps extends Omit<Props<IOption, boolean>, 'theme' | 'placeholder'> {
  label?: string
  error?: string
  indent?: Indent
  isError?: boolean
  isGrouped?: boolean
  isSuccess?: boolean
  isMultiple?: boolean
  helperText?: string
  handleBlur?: (value?: IOption) => void
  isCreatable?: boolean
  placeholder?: string
  handleFocus?: React.FocusEventHandler<HTMLInputElement>
  createOption?: (label: string) => void
  handleChange?: (value: IOption | IOption[]) => void
  onOptionClick?: CreatableSelectOptionClick
  getSelectStyles?: (props: any) => StylesConfig<IOption, boolean, GroupBase<IOption>>
  handleInputChange?: (value: string) => void
  withoutDropDownIndicator?: boolean
}

export const isSelectValueExist = (value: IOption | IOption[] | null): boolean =>
  Array.isArray(value) ? value.length > 0 : !!value?.label

const Select: React.FC<CreatableProps<IOption, boolean, GroupBase<IOption>> & CustomSelectProps> = ({
  // input
  id,
  name,
  error,
  value,
  options = [],
  handleBlur,
  onMenuClose,
  handleFocus,
  handleChange,
  defaultValue,
  handleInputChange,
  // meta
  isError,
  isGrouped,
  isLoading,
  isSuccess,
  isDisabled,
  isMultiple,
  isClearable,
  isCreatable,
  isSearchable = true,
  // view
  label,
  indent,
  required,
  components,
  helperText,
  menuPosition = 'absolute',
  menuPlacement = 'auto',
  getSelectStyles = getDefaultSelectStyles,
  menuPortalTarget = document.querySelector('body'),
  withoutDropDownIndicator,
  ...rest
}) => {
  const [isFocused, setIsFocused] = React.useState(false)
  const [searchedValue, setSearchedValue] = React.useState('')

  const muiTheme = useTheme<Theme>()

  const SelectComponent = isCreatable ? CreatableSelect : ReactSelect

  const isValueExist = React.useMemo<boolean>(
    // @ts-ignore
    () => isSelectValueExist(value) || isSelectValueExist(defaultValue),
    [value, defaultValue]
  )

  const isLabelExist = !!label
  const isLabelShrunk = isLabelExist && (isFocused || isValueExist)

  const onInputChange = (value: string, { action }: { action: string }): void => {
    if (action !== 'input-change') {
      return
    }

    setSearchedValue(value)
    handleInputChange?.(value)
  }

  const onChange = (value: IOption | IOption[]) => {
    if (!value) {
      return
    }

    handleChange?.(value)
    setSearchedValue('')
  }

  const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    !isFocused && setIsFocused(true)
    handleFocus?.(event)
  }

  const onBlur = () => {
    isFocused && setIsFocused(false)
    handleBlur?.()
  }

  const handleMenuClose = () => {
    setSearchedValue('')
    onMenuClose?.()
  }

  return (
    <SelectContainer indent={indent}>
      <FormControl disabled={isDisabled} error={isError} fullWidth>
        {isLabelExist && (
          <InputLabel focused={isFocused} shrink={isLabelShrunk} required={required}>
            {label}
          </InputLabel>
        )}
        <SelectWrapper
          data-testid={name}
          data-entity-id={Array.isArray(value) ? undefined : (value as IOption)?.value}
          $isLabelExist={isLabelExist}
          $withoutDropDown={isDisabled || withoutDropDownIndicator}>
          <SelectComponent
            {...rest}
            id={id}
            name={name}
            value={value}
            onFocus={onFocus}
            onChange={onChange}
            isMulti={isMultiple}
            isLoading={isLoading}
            menuShouldBlockScroll
            isDisabled={isDisabled}
            theme={reactSelectTheme}
            blurInputOnSelect={false}
            isSearchable={isSearchable}
            defaultValue={defaultValue}
            menuPosition={menuPosition}
            onMenuClose={handleMenuClose}
            onInputChange={onInputChange}
            menuPlacement={menuPlacement}
            onBlur={isMobile ? null : onBlur}
            menuPortalTarget={menuPortalTarget}
            components={{ Option, ...components }}
            closeMenuOnSelect={isDesktop || !isMultiple}
            options={isClearable ? getOptionsWithClearValue(options) : options}
            styles={getSelectStyles({
              isError,
              isSuccess,
              isFocused,
              isDisabled,
              isMultiple,
              theme: muiTheme,
              hasInputValue: !!searchedValue
            })}
          />
          <Icon isDisabled={isDisabled} />
          {isError && (
            <ErrorMessageWrapper>
              <ErrorMessage>{error}</ErrorMessage>
            </ErrorMessageWrapper>
          )}
        </SelectWrapper>
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </SelectContainer>
  )
}

export default Select
